"use client"

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { 
  ArrowLeft, 
  PlusIcon, 
  Landmark, 
  Search, 
  RefreshCw, 
  MoreHorizontal, 
  Edit, 
  Trash2, 
  Eye,
  Loader2,
  Check,
  CreditCard
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { useToast } from '@/hooks/use-toast'
import { Badge } from '@/components/ui/badge'
import FormModal from '@/components/FormModal'
import { BankAccountForm } from '@/components/bank-accounts/BankAccountForm'
import BankAccountsService, { BankAccount } from '@/api/bankAccounts.service'
import BusinessService from '@/api/businesses.service'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface BankAccountsProps {
  publicId: string | undefined;
  businessId?: number;
  businessName?: string;
}

export default function BankAccounts({ publicId, businessId: propBusinessId, businessName: propBusinessName }: BankAccountsProps) {
  const [business, setBusiness] = useState<any>(null);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingBusiness, setIsLoadingBusiness] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState<BankAccount | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const { toast } = useToast()

  // Fetch business data if publicId is provided
  useEffect(() => {
    if (!publicId && !propBusinessId) {
      setIsLoadingBusiness(false);
      return;
    }

    if (propBusinessId && propBusinessName) {
      setBusiness({ id: propBusinessId, name: propBusinessName });
      setIsLoadingBusiness(false);
      return;
    }

    async function fetchBusiness() {
      setIsLoadingBusiness(true);
      try {
        const response = await BusinessService.getBusinessByPublicId(publicId as string);
        if (response.data.result === 'invalid') {
          window.location.href = '/logout';
          return;
        }
        if (response.data.result === 'success') {
          setBusiness(response.data.business);
        } else {
          toast({
            title: "Error",
            description: response.data.message || "Failed to load business details",
            variant: "destructive",
          });
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to load business details",
          variant: "destructive",
        });
      } finally {
        setIsLoadingBusiness(false);
      }
    }

    fetchBusiness();
  }, [publicId, propBusinessId, propBusinessName, toast]);

  // Fetch bank accounts after business data is loaded
  useEffect(() => {
    if (!business) return;
    
    fetchBankAccounts();
  }, [business]);

  const fetchBankAccounts = async () => {
    if (!business) return;
    
    setIsLoading(true);
    setError(null);
    try {
      const response = await BankAccountsService.getBusinessBankAccounts(business.publicId);
      if (response.data.result === 'invalid') {
        window.location.href = '/logout';
        return;
      }
      if (response.data.result === 'success') {
        setBankAccounts(response.data.businessBankAccounts || []);
      } else {
        setError(response.data.message || 'Failed to load bank accounts');
        toast({
          title: "Error",
          description: response.data.message || "Failed to load bank accounts",
          variant: "destructive",
        });
      }
    } catch (error) {
      setError('An unexpected error occurred');
      toast({
        title: "Error",
        description: "An unexpected error occurred while loading bank accounts",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!selectedAccount) return;
    
    setIsDeleting(true);
    try {
      const response = await BankAccountsService.deleteBankAccount(selectedAccount.id);
      if (response.data.result === 'success') {
        setBankAccounts(bankAccounts.filter(account => account.id !== selectedAccount.id));
        toast({
          title: "Success",
          description: "Bank account deleted successfully",
        });
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to delete bank account",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred while deleting the bank account",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
      setSelectedAccount(null);
    }
  };

  // Show View/Edit modal for an account
  const handleViewEdit = (account: BankAccount) => {
    setSelectedAccount(account);
    setIsEditModalOpen(true);
  };

  // Confirm delete dialog
  const handleDeleteConfirm = (account: BankAccount) => {
    setSelectedAccount(account);
    setIsDeleteDialogOpen(true);
  };

  // Filter bank accounts based on search query
  const filteredAccounts = searchQuery
    ? bankAccounts.filter(account => 
        account.accountName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        account.bankName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        account.accountNumber.includes(searchQuery)
      )
    : bankAccounts;

  // Mask account number for display
  const maskAccountNumber = (accountNumber: string) => {
    if (accountNumber.length <= 4) return accountNumber;
    return '**' + accountNumber.slice(-4);
  };

  if (isLoadingBusiness) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">Loading business details...</span>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="text-center py-8">
        <div className="text-destructive mb-2">Business not found</div>
        <Button onClick={() => window.history.back()} variant="outline" size="sm">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="container py-4 sm:py-8 mx-auto px-4 sm:px-6 max-w-7xl">
      <div className="flex flex-col space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => window.history.back()} 
              variant="ghost" 
              size="sm"
              className="text-muted-foreground hover:text-foreground h-9"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Bank Accounts</h1>
            <Badge variant="outline" className="ml-1">
              {business.name}
            </Badge>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Button 
              onClick={fetchBankAccounts} 
              variant="outline" 
              size="sm"
              className="text-muted-foreground h-9 justify-center"
              disabled={isLoading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              onClick={() => setIsAddModalOpen(true)} 
              className="bg-gradient-to-r from-primary to-primary/90 text-white shadow-sm hover:shadow-md transition-all h-9 justify-center"
            >
              <PlusIcon className="w-4 h-4 mr-2" /> Add Bank Account
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <Card className="shadow-sm border-slate-200/60 overflow-hidden">
          <CardHeader className="pb-3 px-4 sm:px-6">
            <CardTitle className="text-lg font-medium flex items-center gap-2">
              <Landmark size={18} className="text-muted-foreground" />
              Business Bank Accounts
            </CardTitle>
          </CardHeader>
          <Separator />
          <CardContent className="p-4 sm:p-6">
            <div className="mb-6 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search by account name, bank name, or account number..."
                className="pl-10 h-10 bg-slate-50 border-slate-200 focus-visible:ring-primary/50"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2 text-muted-foreground">Loading bank accounts...</span>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <div className="text-destructive mb-2">{error}</div>
                <Button onClick={fetchBankAccounts} variant="outline" size="sm">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
              </div>
            ) : bankAccounts.length === 0 ? (
              <div className="text-center py-12 border border-dashed rounded-md bg-slate-50">
                <Landmark className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-muted-foreground mb-2">No Bank Accounts</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Add bank accounts to receive payments from your customers.
                </p>
                <Button onClick={() => setIsAddModalOpen(true)}>
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add Bank Account
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-4 sm:-mx-6">
                <div className="inline-block min-w-full align-middle">
                  <Table className="min-w-full">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Account Name</TableHead>
                        <TableHead>Bank Name</TableHead>
                        <TableHead>Account Number</TableHead>
                        <TableHead>Branch Code</TableHead>
                        <TableHead>Account Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAccounts.map((account) => (
                        <TableRow key={account.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <CreditCard className="h-4 w-4 text-muted-foreground" />
                              {account.accountName}
                              {account.isPrimary && (
                                <Badge variant="outline" className="ml-1 bg-green-50 text-green-700 border-green-200 text-xs">
                                  <Check className="h-3 w-3 mr-1" />
                                  Primary
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{account.bankName}</TableCell>
                          <TableCell>{maskAccountNumber(account.accountNumber)}</TableCell>
                          <TableCell>{account.branchCode}</TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="capitalize">
                              {account.accountType}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              Active
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-[160px]">
                                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleViewEdit(account)} className="cursor-pointer">
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleViewEdit(account)} className="cursor-pointer">
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit Account
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteConfirm(account)} 
                                  className="cursor-pointer text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Bank Account Modal */}
      <FormModal
        open={isAddModalOpen}
        setOpen={setIsAddModalOpen}
        title="Add Bank Account"
      >
        <BankAccountForm 
          businessId={business.publicId} 
          onSuccess={() => {
            setIsAddModalOpen(false)
            fetchBankAccounts()
          }} 
        />
      </FormModal>

      {/* Edit Bank Account Modal */}
      <FormModal
        open={isEditModalOpen}
        setOpen={setIsEditModalOpen}
        title="Edit Bank Account"
      >
        {selectedAccount && (
          <BankAccountForm 
            businessId={business.publicId} 
            bankAccount={selectedAccount}
            onSuccess={() => {
              setIsEditModalOpen(false)
              fetchBankAccounts()
            }} 
          />
        )}
      </FormModal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the bank account
              {selectedAccount ? ` "${selectedAccount.accountName}"` : ''}. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteAccount}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
} 